import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import {
  AIButtonContainedLight,
  AIButtonIconGrey,
  AIButtonTextGrey,
} from '../../AIButton'
import { Avatar } from '../../Avatar'
import { ICON_HEIGHT_24 } from '../../Icon'
import Text from '../../Text'

import {
  QA_NAME_AI_DRAFT_PANEL_APPLY_BUTTON,
  QA_NAME_AI_DRAFT_PANEL_CLOSE_BUTTON,
  QA_NAME_AI_DRAFT_PANEL_COPY_BUTTON,
  QA_NAME_AI_DRAFT_PANEL_DISCARD_BUTTON,
  QA_NAME_AI_DRAFT_PANEL_INSERT_BELOW_BUTTON,
  QA_NAME_AI_DRAFT_PANEL_REGENERATE_BUTTON,
  QA_NAME_AI_DRAFT_PANEL_REPLACE_BUTTON,
} from './AITextGenerationDraftPanel.constants'
import { AITextGenerationDraftPanelSkeleton } from './AITextGenerationDraftPanelSkeleton'

import * as styles from './AITextGenerationDraftPanel.module.css'

import CloseIcon from '../../../icons/close.svg'
import CopyIcon from '../../../icons/copy.svg'
import DoneIcon from '../../../icons/done.svg'
import RestoreIcon from '../../../icons/restore.svg'
import AvatarIcon from '../../../icons/unique/ai/avatar.svg'

function AITextGenerationDraftPanel({
  draftSubject,
  generatedText,
  hasExistingContent,
  isCopied,
  isError,
  isLoading,
  promptText,
  showAiNotice,
  onApply,
  onCopy,
  onDiscard,
  onInsertBelow,
  onRegenerate,
  onReplace,
}) {
  const { t } = useTranslation()

  return isLoading ? (
    <AITextGenerationDraftPanelSkeleton />
  ) : (
    <div className={classNames(styles.panel, { [styles.panelError]: isError })}>
      <div className={styles.header}>
        <Avatar
          Icon={AvatarIcon}
          iconSize={ICON_HEIGHT_24}
          size={ICON_HEIGHT_24}
        />
        <Text isSecondary className={styles.promptPreview}>
          {promptText}
        </Text>
        <AIButtonIconGrey
          Icon={CloseIcon}
          qaName={QA_NAME_AI_DRAFT_PANEL_CLOSE_BUTTON}
          onClick={onDiscard}
        />
      </div>

      <div className={styles.content}>
        {draftSubject ? (
          <Text className={styles.subjectPreview}>
            {`${t('AITextGeneration.GeneratedSubjectTitleKey')}: '${draftSubject}'`}
          </Text>
        ) : null}

        <Text
          Element='div'
          dangerouslySetInnerHTML={{ __html: generatedText }}
        />

        {showAiNotice ? (
          <Text isSecondary className={styles.aiNotice}>
            {t('AITextGeneration.GeneratedByAINoticeKey')}
          </Text>
        ) : null}
      </div>

      <div className={styles.divider} />

      <div className={styles.actions}>
        <div className={styles.leftActions}>
          <AIButtonTextGrey
            beforeIconProps={{ Icon: isCopied ? DoneIcon : CopyIcon }}
            disabled={isCopied}
            qaName={QA_NAME_AI_DRAFT_PANEL_COPY_BUTTON}
            text={
              isCopied
                ? t('AITextGeneration.CopiedButtonKey')
                : t('AITextGeneration.CopyButtonKey')
            }
            onClick={onCopy}
          />

          <AIButtonTextGrey
            beforeIconProps={{ Icon: RestoreIcon }}
            qaName={QA_NAME_AI_DRAFT_PANEL_REGENERATE_BUTTON}
            text={t('AITextGeneration.RegenerateButtonKey')}
            onClick={onRegenerate}
          />
        </div>

        <div className={styles.rightActions}>
          <AIButtonTextGrey
            qaName={QA_NAME_AI_DRAFT_PANEL_DISCARD_BUTTON}
            text={t('AITextGeneration.DiscardButtonKey')}
            onClick={onDiscard}
          />

          {hasExistingContent ? (
            <>
              <AIButtonTextGrey
                qaName={QA_NAME_AI_DRAFT_PANEL_INSERT_BELOW_BUTTON}
                text={t('AITextGeneration.InsertBelowButtonKey')}
                onClick={onInsertBelow}
              />

              <AIButtonContainedLight
                qaName={QA_NAME_AI_DRAFT_PANEL_REPLACE_BUTTON}
                text={t('AITextGeneration.ReplaceButtonKey')}
                onClick={onReplace}
              />
            </>
          ) : (
            <AIButtonContainedLight
              qaName={QA_NAME_AI_DRAFT_PANEL_APPLY_BUTTON}
              text={t('AITextGeneration.ApplyButtonKey')}
              onClick={onApply}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default AITextGenerationDraftPanel
